/**
 * /field/downhole/work — the open work orders assigned to this hand, inspections
 * among them (an inspection is a work order of type inspection,
 * recertification or magnaflux; api#905, no inspections table).
 *
 * "ASSIGNED TO ME" IS THE API'S OWN FILTER: `GET /work-orders?open=true&
 * assigned_to=me` (api#914), where `me` is the signed-in user on the server.
 * The screen selects nothing itself.
 *
 * An unassigned order — every order a teardown opens starts unassigned — is not
 * shown as anybody's. Assigning work is the office's call; the empty state
 * says so rather than reading as "nothing to do in the shop".
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { useDownholeWorkOrders } from '../../../hooks/useDownhole';
import { formatInstantTenantDay } from '../../../lib/calendar-date';
import { DH_PRIORITY_LABEL, DH_WORK_ORDER_STATUS_SKIN, DH_WORK_TYPE_LABEL, dhLabel } from '../../../lib/downhole-labels';
import { DhBadge } from '../DownholeBits';
import { FieldCard, FieldEmpty, FieldQueryState, FieldTitle } from './FieldBits';

const PRIORITY_RANK: Record<string, number> = { critical: 0, high: 1, normal: 2, low: 3 };

export default function FieldWorkScreen() {
  const query = useDownholeWorkOrders({ open: 'true', assigned_to: 'me' });
  const tz = query.data?.meta.time_zone ?? null;
  const mine = (query.data?.data ?? [])
    .sort((a, b) => (PRIORITY_RANK[a.priority ?? ''] ?? 9) - (PRIORITY_RANK[b.priority ?? ''] ?? 9));

  return (
    <div data-testid="downhole-field-work">
      <FieldTitle title="My work">
        <p className="mt-1 text-sm text-fieldiq-text">Open work orders and inspections assigned to you.</p>
      </FieldTitle>
      <FieldQueryState query={query} subject="your work orders">
        {mine.length === 0 ? (
          <FieldEmpty>No open work orders are assigned to you. The office assigns shop work.</FieldEmpty>
        ) : (
          <div className="space-y-2">
            {mine.map((w) => (
              <Link key={w.id} to={`/field/downhole/work/${w.id}`} className="block">
                <FieldCard className="flex items-center gap-3 px-4 py-3 active:bg-fieldiq-dark/50">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-extrabold text-fieldiq-accent-signal">{w.wo_number ?? '—'}</span>
                      <DhBadge map={DH_WORK_ORDER_STATUS_SKIN} value={w.status} />
                    </div>
                    <p className="mt-1 text-sm font-bold text-white">
                      {dhLabel(DH_WORK_TYPE_LABEL, w.work_type)} · {w.component_serial ?? '—'}
                    </p>
                    <p className="truncate text-xs text-fieldiq-text">{w.description || '—'}</p>
                    <p className="mt-0.5 text-xs text-fieldiq-muted">
                      {dhLabel(DH_PRIORITY_LABEL, w.priority)} priority · due {formatInstantTenantDay(w.due_date, tz) ?? '—'}
                    </p>
                  </div>
                  <ChevronRight className="size-5 shrink-0 text-fieldiq-muted" aria-hidden="true" />
                </FieldCard>
              </Link>
            ))}
          </div>
        )}
      </FieldQueryState>
    </div>
  );
}
